import { memo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ConnectionIcon from "./ConnectionIcon";
import MCPItemActions from "./MCPItemActions";
import MCPStatusBadge, { type MCPBadgeStatus } from "./MCPStatusBadge";
import { useMCPConnectionTest, MCP_CONNECTION_TEST_QUERY_KEY } from "../hooks/useMCPConnectionTest";
import { useUpdateMCPConnection } from "../hooks/useUpdateMCPConnection";
import type { MCPConnection } from "../types";

type Props = {
  connection: MCPConnection;
  isSelected: boolean;
  canManage: boolean;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
};

const MCPListItem = ({ connection, isSelected, canManage, onSelect, onEdit, onDelete }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateMutation = useUpdateMCPConnection();
  const isActive = connection.status === "online";
  const { status, errorMessage, authRequired, refetch } = useMCPConnectionTest(connection.id);
  const badge: MCPBadgeStatus = !isActive
    ? "disabled"
    : connection.needsAttention || authRequired
      ? "needsAttention"
      : status;
  const statusTitle = errorMessage ?? undefined;

  const handleSelect = useCallback(() => onSelect(connection.id), [connection.id, onSelect]);
  const handleEdit = useCallback(() => onEdit(connection.id), [connection.id, onEdit]);
  const handleDelete = useCallback(() => onDelete(connection.id), [connection.id, onDelete]);
  const handleToggleActive = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (updateMutation.isPending) return;
    updateMutation.mutate(
      { id: connection.id, payload: { is_active: !isActive } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [...MCP_CONNECTION_TEST_QUERY_KEY, connection.id] });
        },
      },
    );
  }, [connection.id, isActive, queryClient, updateMutation]);

  return (
    <li
      onClick={handleSelect}
      className={`group flex cursor-pointer items-center gap-3 rounded-[6px] border px-1 py-2 transition-colors ${
        isSelected
          ? "border-border bg-accent"
          : !isActive
            ? "border-border bg-card hover:bg-accent/60"
            : "border-transparent hover:border-border hover:bg-accent/60"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <ConnectionIcon connection={connection} size="sm" />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-foreground">{connection.name}</div>
          <div className="truncate text-xs text-muted-foreground">{connection.createdAt}</div>
        </div>
      </div>
      <MCPStatusBadge status={badge} title={statusTitle} />
      <button
        type="button"
        role="switch"
        aria-checked={isActive}
        aria-label={isActive ? t("mcpPage.actions.disable") : t("mcpPage.actions.enable")}
        disabled={updateMutation.isPending}
        onClick={handleToggleActive}
        className={`flex h-[18px] w-[33px] shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
          isActive ? "justify-end bg-primary" : "justify-start bg-muted"
        }`}
      >
        <span className="size-3.5 rounded-full bg-white shadow-sm" />
      </button>
      <MCPItemActions
        canManage={canManage}
        onViewDetails={handleSelect}
        onEditDetails={handleEdit}
        onRefresh={refetch}
        onDelete={handleDelete}
      />
    </li>
  );
};

export default memo(MCPListItem);
